/**
 * The brand mark for a service, as inline SVG.
 *
 * ⚠️ WHY THESE ARE BUNDLED RATHER THAN FETCHED.
 * The handoff package's icons are 264 BYTES each — two paths on a 32-unit
 * grid, no script, no external font, no <image> href. At that size a network
 * round trip costs more than the asset, and inlining removes an entire class
 * of problem: no upload, no storage, no CDN, and no SVG served from Boxy's own
 * origin (which is stored XSS the moment anyone uploads a hostile one — the
 * carrier-logo proxy streams the stored MIME straight back, so it would).
 *
 * ⚠️ WHAT THIS DELIBERATELY DOES NOT SOLVE.
 * A service ops adds tomorrow through `POST /trusted/service-types` has no
 * glyph here until the dashboard ships again — and "adding a service is one
 * INSERT, never a deploy" is the whole design principle of the catalogue. So
 * this is the FLOOR, not the ceiling: an uploaded icon, when that path exists,
 * must win over this map. Until then an unknown code renders NOTHING rather
 * than a wrong mark or a broken image.
 *
 * `currentColor` throughout: the package ships black/white/yellow variants, but
 * a fixed #111111 would be invisible the day this row is selected or inverted.
 * Inheriting the text colour gets every variant for free and cannot go wrong.
 */
interface ServiceIconProps {
  code: string;
  className?: string;
}

/** Shared by both marks — the diamond outline. */
const DIAMOND = (
  <path
    d='M16 3.2 L28.8 16 L16 28.8 L3.2 16 Z'
    fill='none'
    stroke='currentColor'
    strokeWidth={3.2}
    strokeLinejoin='round'
  />
);

// "Weight above = Select. Weight below = Save." — the handoff README. The two
// marks differ ONLY in which half is filled, so they must stay side by side.
const GLYPHS: Record<string, React.ReactNode> = {
  boxy_select: (
    <>
      <path d='M16 4 L28 16 H4 Z' fill='currentColor' />
      {DIAMOND}
    </>
  ),
  boxy_save: (
    <>
      <path d='M4 16 H28 L16 28 Z' fill='currentColor' />
      {DIAMOND}
    </>
  ),
};

/**
 * Does this service have a bundled mark?
 *
 * Exported so a surface that already has its OWN fallback (the order-details
 * tile shows a generic truck, the carrier row shows initials) can choose
 * between them WITHOUT duplicating the GLYPHS map. `ServiceIcon` returning
 * null is invisible to a caller — it cannot tell "no glyph" from "rendered
 * nothing", and a caller that guesses would put a truck behind a real mark or
 * leave an empty tile. One map, one answer, asked explicitly.
 */
export const hasServiceGlyph = (code?: string | null): boolean =>
  !!code && code in GLYPHS;

const ServiceIcon = ({ code, className }: ServiceIconProps) => {
  const glyph = GLYPHS[code];

  // No glyph is a valid, expected answer — same discipline as an unmeasured
  // metric. A placeholder box would be a claim that something failed to load.
  if (!glyph) return null;

  return (
    <svg
      viewBox='0 0 32 32'
      className={className}
      // Decorative: the service NAME is right beside it and is the accessible
      // label. Announcing the mark as well would read the service twice.
      aria-hidden='true'
      focusable='false'
    >
      {glyph}
    </svg>
  );
};

export default ServiceIcon;
